import { Search, Download } from "lucide-react";

export const MembersTab = ({ members, mq, setMq, admins, promoteEmail, setPromoteEmail, promote, demote, allowlist, setAllowlist, saveAllowlist, exportMembers }) => {
  const filtered = members.filter((m) => {
    const q = mq.trim().toLowerCase();
    if (!q) return true;
    return [m.name, m.email, m.dojo].some((v) => (v || "").toLowerCase().includes(q));
  });

  return (
    <div className="space-y-8">
      <div className="border border-white/10 bg-[#121418] p-8" data-testid="registered-members-section">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div>
            <h3 className="font-serif text-2xl text-[#EBE9E1]">Registered Members</h3>
            <p className="text-[#8A8F98] text-sm mt-1">Everyone who has signed up, with their email and dojo affiliation.</p>
          </div>
          <div className="flex items-center gap-3">
            <span data-testid="members-count" className="text-[#C5A059] font-serif text-2xl">{members.length}</span>
            <div className="relative">
              <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#555]" />
              <input data-testid="members-search" value={mq} onChange={(e) => setMq(e.target.value)}
                placeholder="Search name, email, dojo"
                className="bg-transparent border border-white/20 focus:border-[#C5A059] outline-none pl-9 pr-3 py-2 text-[#EBE9E1] placeholder:text-[#555] rounded-sm text-sm w-64" />
            </div>
            <button data-testid="members-export-btn" onClick={exportMembers} title="Download all members as CSV"
              className="px-4 py-2 font-medium flex items-center gap-2 border border-white/20 text-[#EBE9E1] hover:border-[#C5A059] rounded-sm transition-colors text-sm">
              <Download size={15} /> Export CSV
            </button>
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="members-table">
            <thead>
              <tr className="text-left text-[10px] tracking-widest uppercase text-[#8A8F98] border-b border-white/10">
                <th className="py-3 pr-4 font-medium">Name</th>
                <th className="py-3 pr-4 font-medium">Email</th>
                <th className="py-3 pr-4 font-medium">Dojo</th>
                <th className="py-3 pr-4 font-medium">Role</th>
                <th className="py-3 font-medium">Joined</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((m) => (
                <tr key={m.email} data-testid={`member-row-${m.email}`} className="border-b border-white/5 text-[#EBE9E1]">
                  <td className="py-3 pr-4">{m.name || "—"}</td>
                  <td className="py-3 pr-4 text-[#8A8F98]">{m.email}</td>
                  <td className="py-3 pr-4">{m.dojo || <span className="text-[#555]">—</span>}</td>
                  <td className="py-3 pr-4">
                    <span className={m.role === "admin" ? "text-[#C5A059]" : "text-[#8A8F98]"}>{m.role === "admin" ? "Admin" : "Member"}</span>
                  </td>
                  <td className="py-3 text-[#8A8F98] whitespace-nowrap">{m.created_at ? new Date(m.created_at).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
          {members.length === 0 && <p className="text-[#8A8F98] text-sm py-6 text-center">No members have signed up yet.</p>}
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="border border-white/10 bg-[#121418] p-8">
          <h3 className="font-serif text-2xl text-[#EBE9E1] mb-3">Administrators</h3>
          <p className="text-[#8A8F98] text-sm mb-4">Grant admin access by email. The person must have signed in at least once (Google or email/password) so an account exists.</p>
          <div className="flex gap-2 mb-6">
            <input data-testid="admin-promote-email" value={promoteEmail} onChange={(e) => setPromoteEmail(e.target.value)}
              placeholder="name@example.com"
              className="flex-1 bg-transparent border border-white/20 focus:border-[#C5A059] outline-none px-3 py-2 text-[#EBE9E1] placeholder:text-[#555] rounded-sm text-sm" />
            <button data-testid="admin-promote-btn" onClick={promote} className="btn-primary px-5 py-2 font-medium text-sm">Make Admin</button>
          </div>
          <div className="space-y-2">
            {admins.map((a) => (
              <div key={a.email} className="flex items-center justify-between border-b border-white/5 py-2 text-sm">
                <div className="min-w-0">
                  <p className="text-[#EBE9E1] truncate">{a.name || a.email}</p>
                  <p className="text-[#8A8F98] text-xs truncate">{a.email}</p>
                </div>
                <button data-testid={`admin-demote-${a.email}`} onClick={() => demote(a.email)}
                  className="text-[#8A8F98] hover:text-[#8B2626] text-xs shrink-0 ml-3">Remove</button>
              </div>
            ))}
          </div>
        </div>
        <div className="border border-white/10 bg-[#121418] p-8">
          <h3 className="font-serif text-2xl text-[#EBE9E1] mb-3">Birankai Member Allowlist</h3>
          <p className="text-[#8A8F98] text-sm mb-4">Emails on this list automatically qualify for discounted member pricing. One per line or comma-separated.</p>
          <textarea data-testid="admin-allowlist" value={allowlist} onChange={(e) => setAllowlist(e.target.value)} rows={6}
            placeholder="member1@dojo.com&#10;member2@dojo.com"
            className="w-full bg-transparent border border-white/20 focus:border-[#C5A059] outline-none px-3 py-2 text-[#EBE9E1] placeholder:text-[#555] rounded-sm text-sm mb-4" />
          <button data-testid="admin-save-allowlist" onClick={saveAllowlist} className="btn-primary px-8 py-3 font-medium">Save Allowlist</button>
        </div>
      </div>
    </div>
  );
};
